import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from 'react-i18next';
import BackButton from "../components/BackButton";
import { UpdateAnalytics, UpdateUserLimits } from "../Firebase";
import DownloadBtn from "../assets/download-minimalistic-svgrepo-com.svg";
import DownloadSquare from "../assets/download-square-svgrepo-com.png";

const API_BASE = "https://cl9ibgcm44.execute-api.eu-central-1.amazonaws.com/default";

export default function Chat() {
  const { t } = useTranslation();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [typing, setTyping] = useState(false);
  const [typingMessage, setTypingMessage] = useState("");
  const [chatType, setChatType] = useState("");
  const [isIos, setIsIos] = useState(false);
  const endRef = useRef(null);

  // auto-scroll to latest message
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, typing]);

  function addMessageToArray(sender, message) {
    setMessages((prev) => [...prev, { text: message, sender }]);
  }

  async function downloadImage(imageUrl) {
    const link = document.createElement("a");
    link.href = imageUrl;
    link.download = "generate.png";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    alert(t('portal.chat.imageDownloaded'));
  }

  const detectMobileOS = () => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    if (/iPhone|iPad|iPod/i.test(userAgent)) setIsIos(true);
  };

  // ---------- API HELPERS ----------
  async function callChatEndpoint({ prompt, messages }) {
    const res = await fetch(`${API_BASE}/chat`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ prompt, messages }),
    });

    // Always JSON for /chat (success or blocked)
    const data = await res.json().catch(() => ({}));

    // Treat any blocked OR explicit "18+ content..." text as blocked
    const looksBlockedText = /18\+\s*content\s*is\s*not\s*allowed/i.test(
      data?.text || ""
    );
    if (!res.ok || data?.type === "blocked" || looksBlockedText) {
      return {
        ok: false,
        blocked: data?.type === "blocked" || looksBlockedText,
        reason: data?.reason || "sexual",
        text: data?.text || "Sexual Content not allowed",
      };
    }

    return { ok: true, text: data.text ?? "" };
  }

  async function callImageEndpoint({ prompt }) {
    const res = await fetch(`${API_BASE}/image`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ prompt }),
    });

    const ct = res.headers.get("content-type") || "";

    // Blocked or error in /image returns JSON
    if (ct.includes("application/json")) {
      const data = await res.json().catch(() => ({}));
      const looksBlockedText = /18\+\s*content\s*is\s*not\s*allowed/i.test(
        data?.text || ""
      );
      return {
        ok: false,
        blocked: data?.type === "blocked" || looksBlockedText,
        reason: data?.reason || "sexual",
        text: data?.text || "Sexual Content not allowed",
      };
    }

    if (!res.ok) {
      return {
        ok: false,
        blocked: false,
        reason: "error",
        text: "Image generation failed. Please try again.",
      };
    }

    // Success returns PNG bytes
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    return { ok: true, imageUrl: url };
  }

  // ------------------- MAIN SEND -------------------
  async function sendMessage(message) {
    const userData = JSON.parse(sessionStorage.getItem("AI_UserData") || "{}");
    const phone = userData?.phone;
    const limit = userData?.image_generation_limit ?? 0;

    setTyping(true);
    setTypingMessage(chatType === "image" ? t('portal.chat.generating') : t('portal.chat.typing'));
    setInput("");

    // Initial greeting (even if image mode)
    if (!message) {
      const result = await callChatEndpoint({ prompt: "Hi" });
      if (result.ok) addMessageToArray("bot", result.text);
      else addMessageToArray("bot", t('portal.chat.hello'));
      setTyping(false);
      return;
    }

    // push user bubble first
    addMessageToArray("user", message);

    if (chatType === "image") {
      // limit check → system bubble
      if (limit <= 0) {
        addMessageToArray("system", t('portal.chat.limitReached'));
        setTyping(false);
        return;
      }

      const result = await callImageEndpoint({ prompt: message });

      // blocked (sexual) → system bubble
      if (!result.ok && result.blocked && result.reason === "sexual") {
        addMessageToArray("system", t('portal.chat.sexualContentNotAllowed'));
        setTyping(false);
        return;
      }

      if (!result.ok) {
        console.error(result.text);
        addMessageToArray(
          "system",
          t('portal.chat.imageGenerationFailed')
        );
        setTyping(false);
        return;
      }

      // success
      addMessageToArray("bot", result.imageUrl);
      UpdateUserLimits("image_generation_limit", phone, 1);

      // keep UI in sync locally
      try {
        const updated = { ...(userData || {}) };
        updated.image_generation_limit = Math.max(
          0,
          (updated.image_generation_limit ?? 0) - 1
        );
        sessionStorage.setItem("AI_UserData", JSON.stringify(updated));
      } catch {}

      UpdateAnalytics("image_generation");
      setTyping(false);
      return;
    }

    // ---------- chat mode ----------
    const result = await callChatEndpoint({ prompt: message });

    if (!result.ok && result.blocked && result.reason === "sexual") {
      addMessageToArray("system", t('portal.chat.sexualContentNotAllowed'));
      setTyping(false);
      return;
    }

    if (!result.ok) {
      console.error(result.text);
      addMessageToArray("system", t('portal.chat.somethingWentWrong'));
      setTyping(false);
      return;
    }

    addMessageToArray("bot", result.text);
    UpdateAnalytics("text_chat");
    setTyping(false);
  }

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const chattype = urlParams.get("type");
    setChatType(chattype || "chat");

    (async () => {
      await sendMessage();
    })();

    detectMobileOS();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      className="pt-4"
      style={{ display: "flex", flexDirection: "column", height: "100vh" }}
    >
      <div className="container">
        {chatType === "chat" ? (
          <BackButton title={t('portal.chat.aiChat')} />
        ) : (
          <BackButton title={t('portal.chat.aiImageGenerator')} />
        )}
      </div>

      {/* Scrollable Chat Section */}
      <div
        className="container chat-section d-flex flex-column"
        style={{ height: "75vh", overflow: "auto", paddingBottom: "10rem" }}
      >
        {messages.map((msg, index) => {
          const isUser = msg.sender === "user";
          const isSystem = msg.sender === "system";
          const bg = isUser ? "#d1e7ff" : isSystem ? "#ffe5e5" : "#f1f1f1";
          const color = isSystem ? "#a10000" : "inherit";
          const borderLeft = isSystem ? "4px solid #a10000" : "none";
          const maxWidth = "70%";

          return (
            <div
              className="bold text-dark"
              key={index}
              style={{
                boxShadow: isUser
                  ? "-7px 8px 7px rgba(0, 0, 0, 0.1)"
                  : "7px 8px 7px rgba(0, 0, 0, 0.1)",
                padding: "10px",
                margin: "15px 0",
                fontSize: "1.2rem",
                maxWidth,
                borderRadius: "5px",
                position: "relative",
                alignSelf: isUser ? "flex-end" : "flex-start",
                background: bg,
                color,
                borderLeft,
                textAlign: isUser ? "right" : "left",
              }}
            >
              {isUser ? (
                msg.text
              ) : (
                <>
                  {chatType === "image" && !isSystem
                    ? (() => {
                        const isUrlLike =
                          typeof msg.text === "string" &&
                          (/^blob:|^data:|^https?:\/\//.test(msg.text));
                        const looksLikeImage =
                          isUrlLike &&
                          (msg.text.startsWith("blob:") ||
                            msg.text.startsWith("data:") ||
                            msg.text.match(
                              /\.(png|jpg|jpeg|webp|gif)(\?|#|$)/i
                            ));

                        const isFirstBot = index === 0;

                        if (!isFirstBot && looksLikeImage) {
                          return (
                            <div
                              className="d-flex flex-column align-items-start justify-content-center"
                              style={{ position: "relative" }}
                            >
                              <img
                                className="w-100"
                                style={{ borderRadius: "5px" }}
                                src={msg.text}
                                alt="Generated"
                              />
                              {isIos ? (
                                <div
                                  className="d-flex flex-row align-items-center justify-content-center mt-1"
                                  style={{
                                    position: "absolute",
                                    top: "2px",
                                    left: "5px",
                                    background: "white",
                                    padding: "2px 5px",
                                    borderRadius: "5px",
                                    border: "1px solid #000000a1",
                                  }}
                                >
                                  <img
                                    style={{ width: "15px" }}
                                    src={DownloadSquare}
                                    alt=""
                                  />
                                  <p className="p-1 m-0 text-dark">
                                    {t('portal.chat.holdImageToDownload')}
                                  </p>
                                </div>
                              ) : (
                                <div
                                  className="d-flex flex-row align-items-center justify-content-center mt-1"
                                  style={{
                                    border: "1px solid black",
                                    borderRadius: "5px",
                                    width: "100%",
                                    cursor: "pointer",
                                  }}
                                  onClick={() => downloadImage(msg.text)}
                                >
                                  <h2 className="p-2 m-0">{t('portal.chat.download')}</h2>
                                  <img
                                    style={{ width: "18px" }}
                                    src={DownloadBtn}
                                    alt=""
                                  />
                                </div>
                              )}
                            </div>
                          );
                        }

                        return msg.text;
                      })()
                    : msg.text}
                </>
              )}
            </div>
          );
        })}

        {typing && <div key={Date.now()} className="ChatLoader"></div>}
        <div ref={endRef} />
      </div>

      {/* Fixed Chat Controls */}
      <div
        className="container chat-controls"
        style={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          width: "100%",
          padding: "10px",
          background: "var(--main-color-bg)",
        }}
      >
        <input
          id="messageInput"
          type="text"
          placeholder={t('portal.chat.askAnything')}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) =>
            e.key === "Enter" && input.trim() && !typing && sendMessage(input)
          }
          style={{
            flex: 1,
            padding: "15px",
            borderRadius: "10px",
            outline: "none",
          }}
        />
        <button
          onClick={() => sendMessage(input)}
          className="main-button mt-2 bold"
          disabled={!input.trim() || typing}
        >
          {t('portal.chat.sendMessage')}
        </button>
      </div>
    </div>
  );
}
